import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Video, Swords, Wrench, Users, Trophy, ArrowRight, Skull, Sparkles, Ghost, MessageCircle } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1612703508477-00e02a9b170c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const tools = [
  { to: "/video-ideas", icon: Video, title: "Video Idea Generator", desc: "Endless DBD content concepts across killer, survivor, meme, challenge & educational categories.", span: "lg:col-span-8" },
  { to: "/survivor-maker", icon: Ghost, title: "Survivor Maker", desc: "Upload a selfie and AI turns you into a Dead by Daylight survivor. Great for thumbnails & avatars.", span: "lg:col-span-4" },
  { to: "/entity", icon: MessageCircle, title: "Talk to The Entity", desc: "Chat with the god of the Fog. Ask for killer tips, build advice or anything about the trials.", span: "lg:col-span-4" },
  { to: "/challenges", icon: Swords, title: "Challenge Generator", desc: "Self-imposed trials with perk, map & difficulty restrictions.", span: "lg:col-span-4" },
  { to: "/builds", icon: Wrench, title: "Build Generator", desc: "Synergistic 4-perk builds by killer/survivor & playstyle.", span: "lg:col-span-4" },
  { to: "/community", icon: Users, title: "Community Feed", desc: "Share ideas, builds, clips & creator tips with the fog.", span: "lg:col-span-6" },
  { to: "/leaderboard", icon: Trophy, title: "Creator Leaderboard", desc: "Climb the ranks. Most challenges, top creators, hottest ideas.", span: "lg:col-span-6" },
];

export default function Home() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO} alt="Foggy forest" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background/50" />
        </div>

        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
          <div className="max-w-3xl">
            <div className="fade-up mb-6 inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> AI-Powered Creator Toolkit
            </div>
            <h1 className="fade-up font-display text-6xl leading-[0.95] tracking-wider text-foreground sm:text-7xl md:text-8xl" style={{ animationDelay: "0.1s" }}>
              DBD <span className="text-primary text-glow-red flicker">CREATOR</span> HUB
            </h1>
            <p className="fade-up mt-6 max-w-xl text-lg text-foreground/70 md:text-xl" style={{ animationDelay: "0.2s" }}>
              Your ultimate toolkit for Dead by Daylight creators. Generate video ideas, brutal
              challenges and meta builds — then share them with the community.
            </p>

            <div className="fade-up mt-10 flex flex-wrap gap-4" style={{ animationDelay: "0.3s" }}>
              <Button asChild className="heartbeat rounded-sm bg-primary px-7 py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90">
                <Link to="/video-ideas" data-testid="hero-video-btn"><Video className="mr-2 h-5 w-5" /> Generate Video Idea</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm border-white/15 bg-transparent px-7 py-6 text-sm font-bold uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
                <Link to="/challenges" data-testid="hero-challenge-btn"><Swords className="mr-2 h-5 w-5" /> Create Challenge</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm border-white/15 bg-transparent px-7 py-6 text-sm font-bold uppercase tracking-widest hover:border-primary/50 hover:bg-primary/10">
                <Link to="/builds" data-testid="hero-build-btn"><Wrench className="mr-2 h-5 w-5" /> Explore Builds</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS GRID */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">The Toolkit</p>
            <h2 className="mt-2 font-display text-4xl tracking-wider md:text-5xl">Everything a creator needs</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {tools.map((t, i) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`tool-card-${i}`}
              className={`group relative flex flex-col justify-between overflow-hidden border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 ${t.span} min-h-[200px]`}
            >
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/5 blur-2xl transition-all group-hover:bg-primary/15" />
              <t.icon className="h-9 w-9 text-primary" />
              <div className="mt-6">
                <h3 className="font-display text-2xl tracking-wide">{t.title}</h3>
                <p className="mt-2 max-w-md text-sm text-foreground/60">{t.desc}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Open <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      {!user && (
        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
          <div className="relative overflow-hidden border border-primary/20 bg-card p-10 text-center md:p-16">
            <div className="absolute inset-0 grain opacity-[0.08]" />
            <Skull className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-4 font-display text-4xl tracking-wider md:text-5xl">Track your trials. Climb the ranks.</h2>
            <p className="mx-auto mt-3 max-w-xl text-foreground/60">Create a free profile to save ideas, log completed challenges and rise up the creator leaderboard.</p>
            <Button onClick={() => setAuthOpen(true)} data-testid="cta-signup-btn" className="mt-7 rounded-sm bg-primary px-8 py-6 text-sm font-bold uppercase tracking-widest hover:bg-primary/90">
              Create Free Account
            </Button>
          </div>
        </section>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
